import { useCallback, useEffect, useRef } from 'react'

import { ReactComponent as ArrowUpIcon } from 'assets/images/reports/icons/thin-arrow-up.svg'
import { useAIChat } from 'contexts/AIChatConnectionProvider'
import { withFormsy } from 'formsy-react'
import { PassDownProps } from 'formsy-react/dist/withFormsy'
import { buildTranslate } from 'locales'
import { generateId } from 'utilities/uniqueId'
// import CharacterCounter from 'components/CharacterCounter'
import './ThanxAIPromptInput.scss'

const t = buildTranslate('components.ai.prompt_input')

// Maximum message size for user-typed input (backend allows up to 20000 for picker responses)
const MAX_MESSAGE_SIZE = 5000

export interface ThanxAIPromptInputProps {
  placeholder: string
  submitText?: string
  initialValue?: string
  id?: string
  'aria-describedby'?: string
  isStreaming?: boolean
  awaitingResponse?: boolean
}

type CombinedProps = ThanxAIPromptInputProps & PassDownProps<string>

const ThanxAIPromptInputComponent = ({
  placeholder,
  submitText = t('default_submit_text'),
  initialValue = '',
  setValue,
  value,
  id,
  'aria-describedby': ariaDescribedBy,
  isStreaming = false,
  awaitingResponse = false,
  errorMessage,
}: CombinedProps) => {
  const { isConnected } = useAIChat()
  const currentValue = value || ''
  const hasError = !!errorMessage
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleChange = useCallback(
    (newValue: string) => {
      setValue(newValue)
    },
    [setValue]
  )

  const resizeTextarea = useCallback(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current
      textarea.style.height = 'auto'
      const newHeight = Math.min(textarea.scrollHeight, 216)
      textarea.style.height = `${newHeight}px`

      // Enable scrolling when content exceeds max height
      if (textarea.scrollHeight > 216) {
        textarea.style.overflowY = 'auto'
      } else {
        textarea.style.overflowY = 'hidden'
      }
    }
  }, [])

  const handleTextareaChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = event.target.value
      handleChange(newValue)

      // Auto-resize textarea after a small delay to ensure DOM has updated
      setTimeout(() => {
        resizeTextarea()
      }, 0)
    },
    [handleChange, resizeTextarea]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter') {
        if (event.shiftKey) {
          // Allow Shift+Enter for multiline input (default behavior)
          return
        } else {
          // Enter alone submits the form
          event.preventDefault()

          // Don't submit if not connected, streaming, awaiting response, empty, or too long
          if (
            !isConnected ||
            isStreaming ||
            awaitingResponse ||
            !currentValue.trim() ||
            currentValue.length > MAX_MESSAGE_SIZE
          ) {
            return
          }

          // Trigger form submission by clicking the submit button
          const form = event.currentTarget.closest('form')
          if (form) {
            const submitButton = form.querySelector(
              'button[type="submit"]'
            ) as HTMLButtonElement
            if (submitButton && !submitButton.disabled) {
              submitButton.click()
            }
          }
        }
      }
    },
    [currentValue, isStreaming, awaitingResponse, isConnected]
  )

  const generateTextAreaId = useCallback(() => {
    // This is required to make snapshot tests deterministic
    return process.env.NODE_ENV === 'test'
      ? 'test'
      : `thanx-ai-prompt-textarea-${generateId()}`
  }, [])

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue)
    }
  }, [initialValue, setValue])

  // TODO: Replace with useId() once we've upgraded to React 18
  const idRef = useRef<string>()
  if (!idRef.current) {
    idRef.current = generateTextAreaId()
  }
  const textareaId = id || idRef.current

  useEffect(() => {
    resizeTextarea()
  }, [currentValue, resizeTextarea])
  const isEmpty = !currentValue.trim()
  const isDisabled = isEmpty || isStreaming || awaitingResponse || !isConnected
  const charCount = currentValue.length
  const isTooLong = charCount > MAX_MESSAGE_SIZE

  const getSubmitButtonAriaLabel = () => {
    if (!isConnected) {
      return `${submitText} - ${t('disabled_not_connected')}`
    }
    if (isStreaming) {
      return `${submitText} - ${t('disabled_streaming')}`
    }
    if (awaitingResponse) {
      return `${submitText} - ${t('disabled_awaiting_response')}`
    }
    if (isEmpty) {
      return `${submitText} - ${t('disabled_empty_input')}`
    }
    if (isTooLong) {
      return `${submitText} - ${t('disabled_too_long')}`
    }
    return submitText
  }

  return (
    <div className="thanx-ai-prompt-input__container">
      <textarea
        ref={textareaRef}
        id={textareaId}
        placeholder={placeholder}
        value={currentValue}
        onChange={handleTextareaChange}
        onKeyDown={handleKeyDown}
        className={`thanx-ai-prompt-input__textarea ${
          hasError ? 'thanx-ai-prompt-input__textarea--error' : ''
        }`}
        aria-label={t('aria_label_textarea')}
        aria-describedby={ariaDescribedBy}
        aria-invalid={hasError}
        aria-required={true}
      />
      {hasError && (
        <div className="thanx-ai-prompt-input__error" role="alert">
          {errorMessage}
        </div>
      )}
      {/* <CharacterCounter
        currentCount={charCount}
        maxCount={MAX_MESSAGE_SIZE}
        isError={isTooLong}
        className="thanx-ai-prompt-input__char-count"
      /> */}
      <button
        type="submit"
        disabled={isDisabled || isTooLong}
        className="thanx-ai-prompt-input__submit-btn btn btn-secondary"
        title={submitText}
        aria-label={getSubmitButtonAriaLabel()}
        aria-describedby={textareaId}
      >
        <ArrowUpIcon aria-hidden="true" />
      </button>
    </div>
  )
}

const ThanxAIPromptInput = withFormsy(ThanxAIPromptInputComponent)

export default ThanxAIPromptInput
